import { z } from 'zod/v4';

/**
 * An amfori Site ID: `<country>-<business partner>-<site>`, e.g.
 * `008-000012-001`. Its first two segments are the Business Partner ID's,
 * which ends in `-000` instead. Every stored id has this shape.
 */
export const AMFORI_SITE_ID_PATTERN = /^\d{3}-\d{6}-\d{3}$/;

/**
 * Body for the manual supplier-link write (`PUT /integrations/amfori/suppliers/:companyId`).
 *
 * `amforiSiteId`:
 * - a full (trimmed) amfori Site ID → set/correct the supplier's amfori site
 *   id; its Business Partner ID is derived from it, so a partial id is
 *   rejected;
 * - `null` → unlink (clear the site id).
 *
 * A `…-000` value is rejected: in amfori's `NNN-NNNNNN-NNN` id scheme the `-000`
 * suffix is the **Business Partner** id, never a site — a common copy/paste
 * mistake off the sync report that could never match a synced site.
 */
export const AmforiSupplierLinkUpdateInputSchema = z
  .object({
    amforiSiteId: z
      .string()
      .trim()
      .min(1)
      .regex(AMFORI_SITE_ID_PATTERN, {
        message:
          'amforiSiteId must be a full amfori Site ID (e.g. 008-000012-001).',
      })
      .refine(v => !v.endsWith('-000'), {
        message:
          'amforiSiteId is a Business Partner ID (ends in -000); provide the amfori Site ID (e.g. 008-000012-001).',
      })
      .nullable(),
  })
  .strict();

export type AmforiSupplierLinkUpdateInput = z.infer<
  typeof AmforiSupplierLinkUpdateInputSchema
>;
